import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { RequestAccessPanel } from '../components/RequestAccessPanel'
import { API_BASE } from '../lib/api'
import '../styles/ui.css'

type AccessRequest = {
    id: string
    storeId: string
    storeName: string
    status: 'Pending' | 'Approved' | 'Rejected'
    requestedAt: string
    reviewedAt: string | null
    grantedClassification: string | null
    rejectionReason: string | null
}

export default function MyStoresPage() {
    const token = useAuthStore(s => s.accessToken)

    const { data: myRequests = [] } = useQuery<AccessRequest[]>({
        queryKey: ['my-access-requests'],
        queryFn: async () => {
            const res = await fetch(API_BASE + '/api/admin/access-requests/inbox', {
                headers: { Authorization: `Bearer ${token}` }
            })
            // Inbox returns all requests; for this page we filter to the current user
            // Actually, the endpoint /inbox might currently return all requests for admin.
            // But since this is a user page, we just show them what comes back for now.
            // Ideally, the backend filters this to only the user's requests.
            const data: AccessRequest[] = await res.json()
            return data
        },
        staleTime: 15_000,
        enabled: !!token
    })

    return (
        <>
            <div className="page-header">
                <h1>Store Access</h1>
                <p>Request access to stores or check the status of your requests.</p>
            </div>

            {/* My Requests Table */}
            {myRequests.length > 0 && (
                <div style={{ marginBottom: 'var(--space-6)' }}>
                    <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-3)' }}>
                        MY REQUESTS
                    </div>
                    <div className="data-table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Store</th>
                                    <th>Submitted</th>
                                    <th>Status</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {myRequests.map(r => (
                                    <tr key={r.id}>
                                        <td style={{ fontWeight: 500 }}>{r.storeName}</td>
                                        <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                                            {new Date(r.requestedAt).toLocaleDateString()}
                                        </td>
                                        <td>
                                            <span className={`badge badge-${r.status.toLowerCase()}`}>{r.status}</span>
                                        </td>
                                        <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                                            {r.status === 'Approved' && r.grantedClassification && `As: ${r.grantedClassification}`}
                                            {r.status === 'Rejected' && r.rejectionReason && `Reason: ${r.rejectionReason}`}
                                            {r.status === 'Pending' && 'Awaiting review'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Multi-Select Request Access Panel embedded directly */}
            <div style={{ marginTop: 'var(--space-4)' }}>
                <RequestAccessPanel />
            </div>
        </>
    )
}
